/* eslint-disable react-hooks/exhaustive-deps, react/require-default-props */

/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import {
  Checkbox,
  Icons,
  Option,
  Popover,
  Positions,
  RangeDatePicker,
  RangePickerValueType,
  Select,
  TextInput,
} from '@sede-enterprise/shell-ds-react-framework';

import dayjs from 'dayjs';

import DATEFORMAT from '../../../../utils/dateFormat';
import Button from '../../../../UI/Button';
import { IColumn, IValue } from './Table.type';
import { ColumnTitleContainer, FilterContainer, LayoutBg } from './style';

const DATE_RANGE = 'date-range';

type IFilterValue = IValue | undefined | IValue[];

interface ISortedColumn {
  orderBy: string | null;
  sort: string | null;
}

interface IFilteredColumn {
  fieldName: string;
  value: IFilterValue;
}

interface IColumnTitle {
  column: IColumn;
  sortedColumn: ISortedColumn;
  filteredColumn?: IFilteredColumn | null;
  applySort?: (orderBy: string, sort: string) => void;
  applyFilter?: (
    key: string,
    fieldName: string,
    value: IValue | undefined | IValue[],
    displayLabel?: string,
    displayValue?: string | string[],
  ) => void;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

const { Filter, FilterSquare, ChevronUpDown, ArrowUpSquare, ArrowDownSquare } = Icons;

const ColumnTitle = ({
  column,
  sortedColumn,
  filteredColumn = null,
  applySort,
  applyFilter,
  getPopupContainer,
}: IColumnTitle) => {
  const [sort, setSort] = useState<string | null>(null);
  const [appliedFilter, setAppliedFilter] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<IValue | undefined | IValue[]>(undefined);
  const textInputRef = useRef<HTMLInputElement>(null);

  // Refocuses the text/number filter input after every keystroke, since each re-render of this
  // column header otherwise drops focus back to the document.
  useEffect(() => {
    if (column.type !== 'select' && column.type !== 'checkbox' && column.type !== DATE_RANGE) {
      textInputRef.current?.focus();
    }
  }, [searchValue]);

  const getOrder = (currentOrder) => {
    switch (currentOrder) {
      case 'asc':
        return 'desc';
      case 'desc':
        return '';
      default:
        return 'asc';
    }
  };

  const handleSortOnClick = () => {
    const order = getOrder(sort);
    setSort(order);
    applySort?.(column.accessorKey, order);
  };

  useEffect(() => {
    const order = column.accessorKey === sortedColumn?.orderBy ? sortedColumn?.sort : null;
    setSort(order);
  }, [sortedColumn]);

  useEffect(() => {
    if (filteredColumn?.value) {
      setSearchValue(filteredColumn.value);
      setAppliedFilter(true);
    } else {
      setAppliedFilter(false);
      switch (column.type) {
        case 'select':
          setSearchValue(undefined);
          break;
        case 'number':
        case 'text':
          setSearchValue('');
          break;
        case 'checkbox':
          setSearchValue([]);
          break;
        case DATE_RANGE:
          setSearchValue([]);
          break;
        default:
          setSearchValue('');
          break;
      }
    }
  }, [filteredColumn]);

  const handleOnCheckedChange = (checked: boolean, value: number | string) => {
    if (typeof searchValue === 'object') {
      if (checked) {
        setSearchValue([...searchValue, value]);
      } else {
        setSearchValue(searchValue.filter((_) => _ !== value));
      }
    }
  };

  const handleOnChangeSelect = (value: number) => {
    setSearchValue(value);
  };

  const handleOnChangeTextInput = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleOnChangeDateRange = (value: RangePickerValueType) => {
    setSearchValue(value ? [dayjs(value[0]).toISOString(), dayjs(value[1]).toISOString()] : []);
  };

  const getDisplayValueForSelect = () => {
    if (column.mode === 'multiple' && typeof searchValue === 'object') {
      const result = column.optionsData?.filter((_) => searchValue.includes(_.id));
      const sortedResult = result?.sort(
        (a, b) => searchValue.indexOf(a.id) - searchValue.indexOf(b.id),
      );
      return sortedResult ? sortedResult.map((_) => _.name) : '';
    }

    const otherResult = column.optionsData?.find((_) => _.id === searchValue);
    return otherResult ? otherResult.name : '';
  };

  const getDisplayValueForCheckbox = () => {
    if (typeof searchValue === 'object') {
      const result = column.optionsData?.filter((_) => searchValue.includes(_.id));
      const sortedResult = result?.sort(
        (a, b) => searchValue.indexOf(a.id) - searchValue.indexOf(b.id),
      );
      return sortedResult ? sortedResult.map((_) => _.name) : '';
    }
    return '';
  };

  const handleSearchOnClick = () => {
    let displayValue: string | string[] = '';
    const { yearMonthDayWithoutDash } = DATEFORMAT;

    if (column.type === 'select') {
      displayValue = getDisplayValueForSelect();
    } else if (column.type === 'checkbox') {
      displayValue = getDisplayValueForCheckbox();
    } else if (column.type === DATE_RANGE) {
      displayValue = `${dayjs(searchValue?.[0]).format(
        yearMonthDayWithoutDash,
      )} - ${dayjs(searchValue?.[1]).format(yearMonthDayWithoutDash)}`;
    } else {
      displayValue = String(searchValue ?? '');
    }

    applyFilter?.(
      column.accessorKey,
      column?.filter?.key ?? '',
      searchValue,
      column?.filter?.label,
      displayValue,
    );
    setAppliedFilter(true);
  };

  const disableApplyFilter = () => {
    if (typeof searchValue === 'object' && searchValue) {
      return searchValue?.length === 0;
    }
    return !searchValue;
  };

  const renderFilterOption = () => {
    const { type, optionsData = [] } = column;
    switch (column.type) {
      case 'select':
        return (
          <Select
            onChange={handleOnChangeSelect}
            value={searchValue as number}
            data-testid={`select-${column.accessorKey}`}
            placeholder='-- Select --'
            size='small'
            allowClear={false}
            mode={column.mode ?? 'single'}
            maxTagCount={1}
          >
            {' '}
            {optionsData.map((item) => (
              <Option
                key={`select-option-${column.accessorKey}-${item.id}-${item.name}`}
                value={item.id}
              >
                {item.name}
              </Option>
            ))}
          </Select>
        );
      case 'number':
        return (
          <TextInput
            ref={textInputRef}
            type={type}
            placeholder='Search'
            onChange={handleOnChangeTextInput}
            value={searchValue as number}
            size='small'
          />
        );
      case DATE_RANGE:
        return (
          <RangeDatePicker
            size='small'
            placeholder={['From Date', 'To Date']}
            aria-label='Date mode'
            picker='date'
            value={
              typeof searchValue === 'object' && searchValue.length > 1
                ? [dayjs(searchValue[0]), dayjs(searchValue?.[1])]
                : null
            }
            onChange={handleOnChangeDateRange}
          />
        );
      case 'checkbox':
        return (
          <div className='filter-checkbox'>
            {' '}
            {optionsData.map((item) => (
              <Checkbox
                key={`checkbox-option-${column.accessorKey}-${item.id}-${item.name}`}
                label={item.name}
                disabled={!item.isActive}
                checked={typeof searchValue === 'object' ? searchValue.includes(item.id) : false}
                onChange={(event) => handleOnCheckedChange(event.target.checked, item.id)}
              />
            ))}
            <Button size='small' onClick={handleSearchOnClick} disabled={disableApplyFilter()}>
              Apply
            </Button>
          </div>
        );
      default:
        return (
          <TextInput
            ref={textInputRef}
            type='text'
            placeholder='Search'
            onChange={handleOnChangeTextInput}
            value={searchValue as string}
            size='small'
          />
        );
    }
  };

  const renderSortIcon = (sortType: string | null) => {
    switch (sortType) {
      case 'asc':
        return <ArrowUpSquare height={15} width={15} />;
      case 'desc':
        return <ArrowDownSquare height={15} width={15} />;
      default:
        return <ChevronUpDown height={15} width={15} />;
    }
  };

  return (
    <ColumnTitleContainer
      color={LayoutBg}
      className={
        column?.justifyContent
          ? `justify-content-${column?.justifyContent}`
          : `justify-content-center`
      }
      style={{ width: column?.parentwidth ?? 'auto' }}
    >
      <div
        className={`sorting-wrapper ${column?.sorting ? 'pointer' : ''}`}
        data-testid='sorting'
        style={{ width: column?.width ?? 'auto' }}
      >
        <div className='shell-table-column-title'>{column?.header}</div>
      </div>
      {column?.sorting && (
        <div
          className='shell-table-column-sorter'
          onClick={column?.sorting ? handleSortOnClick : () => {}}
          data-testid={`sort-${column?.accessorKey}`}
        >
          {renderSortIcon(sort)}
        </div>
      )}
      {column?.filter && (
        <Popover
          popup={
            <FilterContainer>
              {renderFilterOption()}
              {column?.type !== 'checkbox' && (
                <Button
                  size='small'
                  className='action-button'
                  onClick={handleSearchOnClick}
                  disabled={disableApplyFilter()}
                >
                  Apply
                </Button>
              )}
            </FilterContainer>
          }
          popupPlacement={Positions.Bottom}
          {...(getPopupContainer ? { getPopupContainer } : {})}
        >
          <span className='filter-button pointer' data-testid={`filter-${column?.accessorKey}`}>
            {appliedFilter ? (
              <FilterSquare height={23} width={23} />
            ) : (
              <Filter height={20} width={20} />
            )}
          </span>
        </Popover>
      )}
    </ColumnTitleContainer>
  );
};

export default ColumnTitle;
